import React, { useMemo } from 'react';
import {
  ComposedChart,
  Line,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { 
  transformStandardsDriftForChart
} from '../../../../utils/qaqc/chartDataTransform';
import {
  augmentRowsZScorePlot,
  Z_SCORE_AXIS_MAX,
  Z_SCORE_AXIS_MIN
} from '../../../../utils/qaqc/chartOutliers';
import { PRINT_COLORS, PRINT_CHART_CONFIG } from '../printTheme';
import PrintChartLegendContent from '../PrintChartLegendContent';

const PrintStandardsDriftChart = ({ data, element, crmCode = 'all' }) => {
  // Transform data for chart
  const chartData = useMemo(() => {
    return transformStandardsDriftForChart(data, element, crmCode);
  }, [data, element, crmCode]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (chartData.length === 0) return null;
    
    const failures = chartData.filter(d => d.status === 'fail').length;
    const warnings = chartData.filter(d => d.status === 'warning').length;
    const passes = chartData.filter(d => d.status === 'pass').length;
    
    return {
      count: chartData.length,
      failures,
      warnings,
      passes,
      passRate: ((passes / chartData.length) * 100).toFixed(1)
    };
  }, [chartData]);

  const plotData = useMemo(
    () =>
      augmentRowsZScorePlot(chartData, 'zScore').map((row) => ({
        ...row,
        ewmaZPlot:
          row.ewmaZ != null &&
          typeof row.ewmaZ === 'number' &&
          !Number.isNaN(row.ewmaZ)
            ? Math.min(Z_SCORE_AXIS_MAX, Math.max(Z_SCORE_AXIS_MIN, row.ewmaZ))
            : row.ewmaZ
      })),
    [chartData]
  );

  const scatterShape = (props) => {
    const { cx, cy, payload } = props;
    if (!payload || cx == null || cy == null) return null;
    const clip = payload.zScoreAxisLabel;
    const low =
      clip && payload.zScore < (payload.plotZScore ?? payload.zScore);
    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={4}
          fill={PRINT_COLORS.scatter}
          fillOpacity={0.65}
        />
        {clip && (
          <text
            x={cx}
            y={low ? cy + 11 : cy - 8}
            fill={PRINT_COLORS.text}
            fontSize={7}
            textAnchor="middle"
          >
            {clip}
          </text>
        )}
      </g>
    );
  };

  if (chartData.length === 0) {
    return (
      <div style={{ width: '100%', padding: '20px', background: PRINT_COLORS.background }}>
        <h3 style={{ color: PRINT_COLORS.text, textAlign: 'center' }}>
          Standards Drift - {element} {crmCode !== 'all' ? `(${crmCode})` : ''}
        </h3>
        <p style={{ color: PRINT_COLORS.text, textAlign: 'center' }}>No data available</p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', padding: '20px', background: PRINT_COLORS.background }}>
      <div style={{ marginBottom: '15px' }}>
        <h3 style={{ color: PRINT_COLORS.text, marginBottom: '5px', fontSize: '16px', fontWeight: 'bold' }}>
          Standards Drift Analysis - {element} {crmCode !== 'all' ? `(${crmCode})` : ''}
        </h3>
        {stats && (
          <div style={{ 
            display: 'flex', 
            gap: '20px', 
            fontSize: '10px', 
            color: PRINT_COLORS.text,
            borderBottom: `1px solid ${PRINT_COLORS.border}`,
            paddingBottom: '8px'
          }}>
            <span><strong>Total:</strong> {stats.count}</span>
            <span style={{ color: PRINT_COLORS.pass }}><strong>Pass:</strong> {stats.passes} ({stats.passRate}%)</span>
            <span style={{ color: PRINT_COLORS.warning }}><strong>Warning:</strong> {stats.warnings}</span>
            <span style={{ color: PRINT_COLORS.fail }}><strong>Fail:</strong> {stats.failures}</span>
          </div>
        )}
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart 
          data={plotData} 
          margin={PRINT_CHART_CONFIG.margin}
        >
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke={PRINT_COLORS.grid} 
          />
          
          <XAxis 
            dataKey="index" 
            stroke={PRINT_COLORS.text}
            tick={{ fill: PRINT_COLORS.text, fontSize: PRINT_CHART_CONFIG.fontSize.tick, angle: -45, textAnchor: 'end' }}
            height={78}
            label={{ 
              value: 'Sample Sequence', 
              position: 'insideBottom', 
              offset: 22,
              style: { fill: PRINT_COLORS.text, fontSize: PRINT_CHART_CONFIG.fontSize.axis, fontWeight: 600 }
            }}
          />
          
          <YAxis 
            stroke={PRINT_COLORS.text}
            tick={{ fill: PRINT_COLORS.text, fontSize: PRINT_CHART_CONFIG.fontSize.tick }}
            label={{ 
              value: 'Z-Score', 
              angle: -90, 
              position: 'insideLeft',
              style: { fill: PRINT_COLORS.text, fontSize: PRINT_CHART_CONFIG.fontSize.axis }
            }}
            domain={[Z_SCORE_AXIS_MIN, Z_SCORE_AXIS_MAX]}
            allowDataOverflow
            tickFormatter={(value) => value.toFixed(2)}
          />
          
          {/* Reference lines for Z-score thresholds */}
          <ReferenceLine 
            y={0} 
            stroke={PRINT_COLORS.referenceLineZero} 
            strokeWidth={PRINT_CHART_CONFIG.strokeWidth.normal}
            label={{ value: 'Target', fill: PRINT_COLORS.text, fontSize: 8 }}
          />
          <ReferenceLine 
            y={2} 
            stroke={PRINT_COLORS.referenceLineWarn} 
            strokeDasharray="5 5"
            label={{ value: '+2SD', fill: PRINT_COLORS.warning, fontSize: 8, position: 'right' }}
          />
          <ReferenceLine 
            y={-2} 
            stroke={PRINT_COLORS.referenceLineWarn} 
            strokeDasharray="5 5"
            label={{ value: '-2SD', fill: PRINT_COLORS.warning, fontSize: 8, position: 'right' }}
          />
          <ReferenceLine 
            y={3} 
            stroke={PRINT_COLORS.referenceLineFail} 
            strokeDasharray="3 3"
            strokeWidth={PRINT_CHART_CONFIG.strokeWidth.thick}
            label={{ value: '+3SD', fill: PRINT_COLORS.fail, fontSize: 8, position: 'right' }}
          />
          <ReferenceLine 
            y={-3} 
            stroke={PRINT_COLORS.referenceLineFail} 
            strokeDasharray="3 3"
            strokeWidth={PRINT_CHART_CONFIG.strokeWidth.thick}
            label={{ value: '-3SD', fill: PRINT_COLORS.fail, fontSize: 8, position: 'right' }}
          />
          
          {/* Z-score scatter points - render first (behind) */}
          <Scatter 
            dataKey="plotZScore" 
            fill={PRINT_COLORS.scatter}
            fillOpacity={0.55}
            shape={scatterShape}
            name="Z-Score"
          />
          
          {/* EWMA line - render last (on top) with transparency */}
          <Line 
            type="monotone" 
            dataKey="ewmaZPlot" 
            stroke={PRINT_COLORS.ewma} 
            strokeWidth={PRINT_CHART_CONFIG.strokeWidth.thick}
            strokeOpacity={0.8}
            dot={false}
            name="EWMA"
          />
          
          <Legend content={<PrintChartLegendContent />} />
        </ComposedChart>
      </ResponsiveContainer>

      <div style={{ 
        marginTop: '2px', 
        fontSize: '8px', 
        color: PRINT_COLORS.text,
        fontStyle: 'italic'
      }}>
        EWMA (Exponentially Weighted Moving Average) detects systematic drift. 
        Values outside ±2SD (orange) indicate warning; outside ±3SD (red) indicate failure.
      </div>
    </div>
  );
};

export default PrintStandardsDriftChart;
